/**
 * UTC MONTH ARITHMETIC, AND NOT ONE CLOCK READ IN THE FILE.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ★★ WHY THE CABINET'S CALENDAR IS ANCHORED ON A MONTH THE READER NAMES, RATHER THAN ON "TODAY"
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * `getAuthorCalendar` REQUIRES BOTH BOUNDS. `authors-api.yaml`: `from` and `to` are both
 * `required: true`, and `:2021` declares a 400 for a range that "is missing a bound, is inverted, or
 * is longer than the allowed span". So this screen cannot do what the Overview does — omit the range
 * and print the one the response reports (`routes/dashboard/DashboardRoute.tsx`). It has to
 * name a window before it can read anything.
 *
 * AND THIS APP HAS NO CLOCK. `features/cabinet/format.ts` states it in terms — "THERE IS NO
 * CLOCK IN THIS APP AND THERE MUST NOT BE" — for two reasons, C11 (`eslint.config.mjs` bans
 * zero-argument `new Date()` across `web/`, with exactly ONE exempt path and it is in `src/`) and
 * D9 (every day boundary on this platform is cut on the SERVER clock). Checked rather than assumed:
 * `grep -rn "clock" web/authors/src` returns comments explaining the absence and no implementation,
 * and the ONE inline `eslint-disable no-restricted-syntax` in the whole repo is
 * `web/admin/src/features/queues/model/clock.ts` — the ADMIN console's single browser tick.
 *
 * ★ SO THE WINDOW IS AN EXPLICIT CHOICE, WHICH IS THE OTHER HALF OF THE RULE `format.ts` ALREADY
 * WRITES: "ranges come from the RESPONSE … **or from an explicit choice**, never from the page."
 * The choice is a UTC month, it lives in the address (`/calendar?month=YYYY-MM`) so it survives a
 * reload, and every function below turns that string into instants by arithmetic alone.
 *
 * ★★ AND THE OPENING MONTH, WHEN THE ADDRESS CARRIES NONE, COMES FROM THE SERVER'S OWN INJECTED
 * CLOCK — NOT FROM THE BROWSER. `getPublishDateSuggestions` takes no range and its window is
 * `[clock.now(), clock.now() + picker_horizon_days]`
 * (`src/modules/editorial/cabinet/publish-date-suggestions.service.ts`, and its header §3:
 * "THE WINDOW STARTS AT `now`, NOT AT `from` … 'ahead' is measured from the server clock"). The
 * EARLIEST date it returns is therefore a server-clock-derived day, and `CLOCK_MODE=test` moves it
 * exactly as it moves everything else. `earliestSuggestedMonth` below is that derivation. The one
 * other "where am I in time" a frozen test clock can reach is `getPaymentsView`'s `computedAt`, the
 * server's own instant (routes/calendar/platform-month.ts): `monthOfInstant` and `dayOfInstant`
 * below cut it as every day here is cut, so the month it opens and the day the grid marks as today
 * are the grid's own.
 *
 * ⚠ IT IS A DERIVATION AND THE SCREEN SAYS SO. The suggestions are ordered "by editorial priority
 * and then proximity" (same header, §1), so the earliest date is the nearest UPCOMING interesting
 * day rather than today; with `picker_suggest_lead_hours` seeded 48
 * (`calendar-policy.reader.ts`, argued at `04-schema.md`) it can be a day or two out,
 * and against a sparse calendar it can be a different month. The screen prints which month it
 * opened and why, and the chooser is right there. An empty suggestion list is a real state: the
 * screen then opens on the server's own month, and asks for one only when that cannot be read
 * either - it never guesses one.
 */
import { i18n } from '../../../i18n';

/** `YYYY-MM`. The screen's whole notion of "where am I", and the only thing in the address. */
export type MonthKey = string;

const MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;

/**
 * Is this a month this module can work with? Anything else — a stray query parameter, a truncated
 * paste — is treated as absent rather than coerced, because a coerced month reads like a chosen one.
 */
export function isMonthKey(value: string | null | undefined): value is MonthKey {
  return typeof value === 'string' && MONTH_PATTERN.test(value);
}

function partsOf(month: MonthKey): { year: number; monthIndex: number } {
  const matched = MONTH_PATTERN.exec(month);
  if (matched === null) {
    throw new Error(`${month} is not a YYYY-MM month key; guard with isMonthKey first.`);
  }
  // `[1]` and `[2]` are present whenever the pattern matched; the `?? ''` is for `noUncheckedIndexedAccess`.
  return {
    year: Number.parseInt(matched[1] ?? '', 10),
    monthIndex: Number.parseInt(matched[2] ?? '', 10) - 1,
  };
}

function pad2(value: number): string {
  return value < 10 ? `0${String(value)}` : String(value);
}

/** The month a `format: date` day or a `ServerTime` instant falls in, cut in UTC like every day on
 *  this platform (D9). A PARSE of a value we were given, which C11 exempts by name. */
export function monthOfInstant(value: string): MonthKey | undefined {
  const parsed = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return `${String(parsed.getUTCFullYear())}-${pad2(parsed.getUTCMonth() + 1)}`;
}

/** `2026-09` → `2026-08`, and `2026-01` → `2025-12`. `Date.UTC` normalises the year rollover. */
export function shiftMonth(month: MonthKey, by: number): MonthKey {
  const { year, monthIndex } = partsOf(month);
  const shifted = new Date(Date.UTC(year, monthIndex + by, 1));
  return `${String(shifted.getUTCFullYear())}-${pad2(shifted.getUTCMonth() + 1)}`;
}

export interface MonthWindow {
  /** Inclusive lower bound, as `getAuthorCalendar` declares it. */
  readonly from: string;
  /** EXCLUSIVE upper bound — `authors-api.yaml`, and the service refuses `from == to` outright
   *  (`author-calendar.service.ts`), so this is the first instant of the NEXT month. */
  readonly to: string;
}

/**
 * The half-open instant pair for a month.
 *
 * ★ THE SPAN IS SAFE AGAINST THE SERVER'S CAP BY CONSTRUCTION. The range is refused, never clamped,
 * when it is longer than `calendar_policy.picker_horizon_days`
 * (`author-calendar.service.ts`), which is seeded 180
 * (`calendar-policy.reader.ts`). The longest month is 31 days, so a one-month window cannot
 * provoke that 400 — which is the reason this screen pages by month rather than by quarter.
 */
export function monthWindow(month: MonthKey): MonthWindow {
  const { year, monthIndex } = partsOf(month);
  return {
    from: new Date(Date.UTC(year, monthIndex, 1)).toISOString(),
    to: new Date(Date.UTC(year, monthIndex + 1, 1)).toISOString(),
  };
}

/** Every UTC day in the month, as `YYYY-MM-DD` — the same spelling `AuthorCalendar.dayLoad[].day`
 *  uses, so a day cell and its load are joined on a string and never on a re-derived Date. */
export function daysOfMonth(month: MonthKey): readonly string[] {
  const { year, monthIndex } = partsOf(month);
  const days: string[] = [];
  for (let day = new Date(Date.UTC(year, monthIndex, 1)); day.getUTCMonth() === monthIndex;) {
    days.push(
      `${String(day.getUTCFullYear())}-${pad2(day.getUTCMonth() + 1)}-${pad2(day.getUTCDate())}`,
    );
    day = new Date(day.getTime() + 86_400_000);
  }
  return days;
}

/**
 * How many blank cells precede the first of the month, with MONDAY as column one.
 *
 * `getUTCDay()` is Sunday-based (0..6); the grid is not. This is a rendering choice and not a
 * platform fact — a week that starts on Monday is what the prototype draws and what the cabinet's
 * readers expect — and it changes no boundary: every DAY is still the server's UTC day.
 */
export function leadingBlanks(month: MonthKey): number {
  const { year, monthIndex } = partsOf(month);
  return (new Date(Date.UTC(year, monthIndex, 1)).getUTCDay() + 6) % 7;
}

/**
 * The cabinet's display language as a locale for `Intl` - its English is American English - read
 * each time a date is drawn, so the calendar follows a change of language and never the browser's.
 */
export function displayLocale(): string {
  return i18n.language === 'en' ? 'en-US' : i18n.language;
}

/** `2026-09` → `September 2026`, in the cabinet's language, never the browser's (design 24, T16).
 *  `timeZone: 'UTC'` so a reader in UTC-5 is not shown the month before at the turn of the month
 *  — the same trap `features/cabinet/format.ts` fixes. */
export function monthLabel(month: MonthKey, locale = displayLocale()): string {
  const { year, monthIndex } = partsOf(month);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, monthIndex, 1)));
}

/** `2026-09-17` → `17`. The number in a grid cell, nothing more. */
export function dayNumber(day: string): string {
  return String(Number.parseInt(day.slice(8, 10), 10));
}

/**
 * The month the platform's own next interesting day falls in — see the file header, §★★.
 *
 * `undefined` when the list is empty, which is a real state and not a fallback: an author whose
 * calendar has nothing seeded ahead of the server's now must be asked for a month rather than
 * handed one this file invented.
 */
export function earliestSuggestedMonth(
  items: ReadonlyArray<{ readonly date: string }> | undefined,
): MonthKey | undefined {
  if (items === undefined || items.length === 0) {
    return undefined;
  }
  // The list is ordered by editorial priority and THEN proximity, so the nearest date is not
  // necessarily first. Taking the minimum is what makes this "the next interesting day" rather
  // than "the most important one in the next six months".
  let earliest = items[0]?.date ?? '';
  for (const item of items) {
    if (item.date < earliest) {
      earliest = item.date;
    }
  }
  return monthOfInstant(earliest);
}

/**
 * The day a `format: date` day or a `ServerTime` instant falls on, `YYYY-MM-DD`, cut in UTC: the
 * calendar `monthOfInstant` cuts the month in and the one `daysOfMonth` spells every cell's key in,
 * so the day the server's clock is in (routes/calendar/platform-month.ts) lands on the cell the
 * grid draws for it - `2026-09-30T22:00:00.000Z` is the 30th here, whatever the reader's offset.
 * A PARSE of a value we were given, which C11 exempts by name.
 */
export function dayOfInstant(value: string): string | undefined {
  const parsed = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return `${String(parsed.getUTCFullYear())}-${pad2(parsed.getUTCMonth() + 1)}-${pad2(parsed.getUTCDate())}`;
}
